import type {
  ApplicationStep,
  Discipline,
  Faq,
  Learnership,
  NavItem,
  Partner,
  ShortProgramme,
  Stat,
} from '../types';

/**
 * Content sourced from the Mokaleng IT website.
 * Replace each export with an API call once the backend endpoints exist.
 */

export const organisation = {
  name: 'Mokaleng IT',
  founder: 'Donald Mokaleng',
  founderTitle: 'Founding Director',
  accreditationBody: 'MICT SETA',
  accreditationNumber: 'ACC/2021/02/1345',
  accreditedSince: 2021,
  yearsExperience: 13,
  location: 'East London, Eastern Cape',
  generalEmail: 'admin@mokalengit.co.za',
  trainingEmail: 'training@mokalengit.co.za',
  phoneDisplay: '081 742 8404',
  phoneDial: '+27817428404',
};

export const navItems: NavItem[] = [
  { label: 'Home', to: '/' },
  { label: 'Learnerships', to: '/learnerships' },
  { label: 'Short programmes', to: '/short-programmes' },
  { label: 'How to apply', to: '/apply' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

export const disciplines: Discipline[] = [
  {
    id: 'dis-technical-support',
    name: 'Technical Support',
    summary: 'Troubleshooting, hardware, and network fundamentals.',
    programmeCount: 1,
    icon: 'support',
    tint: 'bg-brand-100 text-brand-700',
  },
  {
    id: 'dis-system-development',
    name: 'System Development',
    summary: 'Programming, internet software, and systems analysis.',
    programmeCount: 1,
    icon: 'code',
    tint: 'bg-accent-100 text-accent-700',
  },
  {
    id: 'dis-system-support',
    name: 'System Support',
    summary: 'Enterprise systems, servers, and LAN/WAN management.',
    programmeCount: 1,
    icon: 'server',
    tint: 'bg-flare-100 text-flare-700',
  },
  {
    id: 'dis-end-user-computing',
    name: 'End User Computing',
    summary: 'Practical workplace computing built on Power Platform.',
    programmeCount: 1,
    icon: 'desktop',
    tint: 'bg-emerald-100 text-emerald-700',
  },
  {
    id: 'dis-microsoft',
    name: 'Microsoft Certifications',
    summary: 'MCSE, MCSA, and Power Platform specialist tracks.',
    programmeCount: 4,
    icon: 'certificate',
    tint: 'bg-sky-100 text-sky-700',
  },
  {
    id: 'dis-comptia',
    name: 'CompTIA Certifications',
    summary: 'A+ and Network+ hybrid hardware and security training.',
    programmeCount: 2,
    icon: 'network',
    tint: 'bg-rose-100 text-rose-700',
  },
];

export const learnerships: Learnership[] = [
  {
    id: 'lrn-78964',
    title: 'National Certificate: IT Technical Support',
    saqaId: '78964',
    nqfLevel: 4,
    credits: 163,
    durationMonths: 12,
    summary:
      'A foundational pathway into IT that blends theory with hands-on practice, building core competency in IT systems, troubleshooting, and technical support.',
    modules: [
      'Introduction to computer technology',
      'Networking architecture',
      'Computer upgrading for hardware and software',
      'Troubleshooting',
      'Management of networks',
      'Business communication',
      'Mathematics',
    ],
    entryRequirements: [
      'Matric certificate',
      'English NQF 3 (Grade 11)',
      'Mathematics Literacy NQF 3 (Grade 11)',
      'Computer literacy',
    ],
    gradient: 'from-brand-600 via-brand-500 to-accent-500',
    badge: 'Most enrolled',
  },
  {
    id: 'lrn-78965',
    title: 'National Certificate: IT Systems Development',
    saqaId: '78965',
    nqfLevel: 4,
    credits: 165,
    durationMonths: 12,
    summary:
      'A structured framework covering programming principles, internet software development, and systems analysis for learners entering development teams.',
    modules: [
      'Introduction to computer technology',
      'Networking architecture',
      'Computer programming',
      'Internet software development',
      'Analysis of infrastructure',
      'Complaints management',
      'IT systems diagnostics',
    ],
    entryRequirements: [
      'Matric certificate',
      'National Certificate: Information Technology (End User Computing) NQF Level 3',
      'Grade 11 or NQF Level 3 with communication, mathematics, and computer literacy',
    ],
    gradient: 'from-accent-600 via-accent-500 to-flare-400',
  },
  {
    id: 'lrn-48573',
    title: 'National Certificate: IT Systems Support',
    saqaId: '48573',
    nqfLevel: 5,
    credits: 147,
    durationMonths: 12,
    summary:
      'An NQF 5 progression from Technical Support that deepens networking expertise and prepares learners to implement solutions in business environments.',
    modules: [
      'Personal development',
      'Network architecture and standards',
      'Enterprise systems management',
      'Designing a LAN for a departmental office',
      'Client server networking',
      'Monitor, maintain, and test systems',
      'Managing LANs and understanding WANs',
      'Assembling, maintaining, and repairing a server',
    ],
    entryRequirements: [
      'Matric certificate',
      'English NQF 4',
      'Mathematics NQF 4',
    ],
    gradient: 'from-emerald-600 via-emerald-500 to-brand-500',
    badge: 'NQF Level 5',
  },
  {
    id: 'lrn-61591',
    title: 'National Certificate: End User Computing',
    saqaId: '61591',
    nqfLevel: null,
    credits: null,
    durationMonths: 12,
    summary:
      'Practical workplace computing skills, including building solutions on the Microsoft Power Platform.',
    modules: [],
    entryRequirements: [],
    gradient: 'from-flare-500 via-flare-400 to-accent-400',
  },
];

export const shortProgrammes: ShortProgramme[] = [
  {
    id: 'sp-mcse',
    title: 'Microsoft Certified Solution Expert',
    vendor: 'Microsoft',
    durationMonths: 4,
    summary: 'Empower your Microsoft skills and level up your productivity.',
    tint: 'bg-sky-100 text-sky-700',
  },
  {
    id: 'sp-mcsa',
    title: 'Microsoft Certified Solution Associate',
    vendor: 'Microsoft',
    durationMonths: 4,
    summary: 'Empower your Microsoft skills and level up your productivity.',
    tint: 'bg-sky-100 text-sky-700',
  },
  {
    id: 'sp-comptia-a',
    title: 'CompTIA A+',
    vendor: 'CompTIA',
    durationMonths: 4,
    summary:
      'Comprehensive hybrid training in IT troubleshooting, hardware management, and network security.',
    tint: 'bg-rose-100 text-rose-700',
  },
  {
    id: 'sp-comptia-n',
    title: 'CompTIA Network+',
    vendor: 'CompTIA',
    durationMonths: 4,
    summary:
      'A hybrid programme focusing on diagnosing, repairing, and securing computer networks.',
    tint: 'bg-rose-100 text-rose-700',
  },
  {
    id: 'sp-pl200',
    title: 'Power Platform Functional Consultant',
    vendor: 'Microsoft',
    code: 'PL-200',
    durationMonths: 4,
    summary: 'An in-depth understanding of the Microsoft Power Platform.',
    tint: 'bg-accent-100 text-accent-700',
  },
  {
    id: 'sp-pl600',
    title: 'Power Platform Solution Architect',
    vendor: 'Microsoft',
    code: 'PL-600',
    durationMonths: 4,
    summary: 'A solutions approach to the Microsoft Power Platform.',
    tint: 'bg-accent-100 text-accent-700',
  },
];

export const applicationSteps: ApplicationStep[] = [
  {
    id: 'step-1',
    step: 1,
    title: 'Select a course',
    description:
      'Choose the learnership or short programme that matches where you want to work.',
    icon: 'select',
  },
  {
    id: 'step-2',
    step: 2,
    title: 'Collect your documents',
    description:
      'Certified matric certificate (with Mathematics), certified ID copy, CV, and an affidavit confirming you are not on any SETA programme.',
    icon: 'document',
  },
  {
    id: 'step-3',
    step: 3,
    title: 'Apply online',
    description:
      'Fill in the form below and upload your four documents. You get a password straight away; sign in once we accept you.',
    icon: 'mail',
  },
  {
    id: 'step-4',
    step: 4,
    title: 'Start learning',
    description:
      'Once accepted, embark on your journey to knowledge and success.',
    icon: 'check',
  },
];

export const stats: Stat[] = [
  { id: 'st-1', value: '150+', label: 'Learners trained' },
  { id: 'st-2', value: '13', label: 'Years of experience' },
  { id: 'st-3', value: '25+', label: 'Satisfied clients' },
  { id: 'st-4', value: '2021', label: 'MICT SETA accredited' },
];

export const partners: Partner[] = [
  { id: 'pt-1', name: 'Microsoft', sector: 'Technology' },
  { id: 'pt-2', name: 'MICT SETA', sector: 'Accreditation' },
  { id: 'pt-3', name: 'CompTIA', sector: 'Certification' },
  { id: 'pt-4', name: 'Chris Hani Municipality', sector: 'Public sector' },
  { id: 'pt-5', name: 'Great Kei Municipality', sector: 'Public sector' },
  { id: 'pt-6', name: 'Bangani Attorneys', sector: 'Legal' },
  { id: 'pt-7', name: 'Hexana Attorneys', sector: 'Legal' },
  { id: 'pt-8', name: 'Golden Security', sector: 'Security' },
];

export const faqs: Faq[] = [
  {
    id: 'faq-1',
    question: 'What types of ICT training programmes do you offer?',
    answer:
      'We offer a range of customisable ICT learnerships and skills development programmes tailored to meet individual and business needs. Our full qualifications are accredited by MICT SETA, and we also run shorter Microsoft and CompTIA certification programmes.',
  },
  {
    id: 'faq-2',
    question: 'How are the training programmes delivered?',
    answer:
      'Training can be delivered through in-person workshops, virtual sessions, or a blended learning approach, depending on what suits the learner or organisation.',
  },
  {
    id: 'faq-3',
    question: 'Who can benefit from your training services?',
    answer:
      'Our services are designed for individuals seeking to enhance their ICT skills, as well as organisations looking to upskill their workforce.',
  },
  {
    id: 'faq-4',
    question: 'Do you provide certification after completing the training?',
    answer:
      'Yes. We offer recognised certifications on successful completion of our training programmes. Our accredited learnerships lead to SAQA-registered national certificates.',
  },
  {
    id: 'faq-5',
    question: 'How long does a learnership take?',
    answer:
      'All four accredited learnerships run for 12 months. Short programmes and vendor certifications run for approximately 4 months.',
  },
  {
    id: 'faq-6',
    question: 'What does MICT SETA accreditation mean?',
    answer:
      'It confirms that Mokaleng IT complies with the quality standards set by SAQA and the QCTO for education and training. It means the programmes are industry-relevant, meet national standards, and improve your employability in the tech sector.',
  },
];
